import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove';
import SyncIcon from '@mui/icons-material/Sync';
import { useGetIdentity, useNotify } from 'react-admin';

import {
    type ReceiptCategory,
    type ResponsibleUser,
    extractReceipt,
    splitReceiptPdf,
    uploadReceipt,
} from './receiptApi';
import { compressReceiptImages } from './compressReceiptImage';
import {
    ReceiptImageCropDialog,
    type ReceiptCropItem,
} from './ReceiptImageCropDialog';
import { ReceiptFieldsEditor } from './ReceiptFieldsEditor';
import {
    type BulkItem,
    type ReceiptFieldValues,
    BULK_STATUS_META,
    buildReceiptFormData,
    canUpload,
    joinCandidates,
    joinItems,
    statusAfterInput,
    uploadBlockMessage,
} from './bulkReceiptHelpers';

const ACCEPT_TYPES = new Set([
    'image/jpeg',
    'image/png',
    'image/heic',
    'image/webp',
    'application/pdf',
]);

const acceptFile = (file: File) =>
    ACCEPT_TYPES.has(file.type) || /\.(jpe?g|png|heic|webp|pdf)$/i.test(file.name);

const isImageFile = (file: File) =>
    file.type.startsWith('image/') || /\.(jpe?g|png|heic|webp)$/i.test(file.name);

const suggestDescription = (merchant: string, category: string): string =>
    (merchant || category || '').substring(0, 20);

const EMPTY_CROP_ITEMS: ReceiptCropItem[] = [];

const FIELD_KEYS: (keyof ReceiptFieldValues)[] = [
    'allocationType',
    'projectId',
    'projectNo',
    'category',
    'subcategory',
    'merchant',
    'amount',
    'transactionDate',
    'responsibleUserId',
    'description',
];

const newLocalId = () =>
    typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `bulk-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const newBulkItem = (file: File, responsibleUserId: string): BulkItem => ({
    localId: newLocalId(),
    file,
    url: URL.createObjectURL(file),
    isImage: isImageFile(file),
    status: 'pending',
    allocationType: '',
    projectId: '',
    projectNo: null,
    category: '',
    subcategory: '',
    merchant: '',
    amount: '',
    transactionDate: '',
    responsibleUserId,
    description: '',
    proofs: [],
});

const extractedToFields = (
    result: Awaited<ReturnType<typeof extractReceipt>>
): Partial<ReceiptFieldValues> => {
    const merchant = result.merchant || '';
    const category = result.project_id ? '' : result.category || '';
    return {
        merchant,
        amount: result.amount != null ? String(result.amount) : '',
        transactionDate: result.transaction_date || '',
        allocationType: result.project_id ? 'project_cost' : 'company_expense',
        category,
        subcategory: result.project_id ? '' : result.subcategory || '',
        projectId: result.project_id ? String(result.project_id) : '',
        projectNo: null,
        description: suggestDescription(merchant, category),
    };
};

type Props = {
    categories: ReceiptCategory[];
    users: ResponsibleUser[];
    onUploaded?: () => void;
};

export const BulkReceiptUpload = ({ categories, users, onUploaded }: Props) => {
    const notify = useNotify();
    const { identity } = useGetIdentity();
    const [items, setItems] = useState<BulkItem[]>([]);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [previewItem, setPreviewItem] = useState<BulkItem | null>(null);
    const [cropId, setCropId] = useState<string | null>(null);
    const [cropItems, setCropItems] = useState<ReceiptCropItem[]>([]);
    const [uploading, setUploading] = useState(false);
    const [uploadingId, setUploadingId] = useState<string | null>(null);
    const [dragActive, setDragActive] = useState(false);
    // Join picker: the row that receives a page (source) and the page picked to move into it (target).
    const [joinSourceId, setJoinSourceId] = useState<string | null>(null);
    const [joinTargetId, setJoinTargetId] = useState<string | null>(null);

    // Latest items for the async analyze/upload loops (state is async).
    const itemsRef = useRef<BulkItem[]>([]);
    const drainingRef = useRef(false);
    const defaultUserId = identity?.id != null ? String(identity.id) : '';

    const commit = useCallback((next: BulkItem[]) => {
        itemsRef.current = next;
        setItems(next);
    }, []);

    const patchItem = useCallback(
        (id: string, patch: Partial<BulkItem>) => {
            commit(
                itemsRef.current.map(item =>
                    item.localId === id ? { ...item, ...patch } : item
                )
            );
        },
        [commit]
    );

    // Edits from the field editor recompute ready/analyzed live.
    const editItem = useCallback(
        (id: string, fields: ReceiptFieldValues) => {
            commit(
                itemsRef.current.map(item => {
                    if (item.localId !== id) return item;
                    const next = { ...item, ...fields };
                    if (
                        item.split !== 'failed' &&
                        (item.status === 'ready' || item.status === 'analyzed' || item.status === 'error')
                    ) {
                        next.status = statusAfterInput(next);
                        next.error = undefined;
                    }
                    return next;
                })
            );
        },
        [commit]
    );

    const analyzeOne = useCallback(
        async (id: string) => {
            patchItem(id, { status: 'analyzing', error: undefined });
            const target = itemsRef.current.find(item => item.localId === id);
            if (!target) return;
            // Split before analysis so each page gets its own extracted values.
            if (!target.isImage && !target.split) {
                let pages: File[];
                try {
                    pages = await splitReceiptPdf(target.file);
                } catch (error) {
                    patchItem(id, {
                        status: 'error',
                        split: 'failed',
                        error:
                            error instanceof Error ? error.message : 'This PDF could not be split.',
                    });
                    return;
                }
                if (pages.length > 1) {
                    // Replace the PDF with one pending item per page; the drain analyses page 1 next.
                    URL.revokeObjectURL(target.url);
                    commit(
                        itemsRef.current.flatMap(item =>
                            item.localId === id
                                ? pages.map(file => ({
                                      ...newBulkItem(file, item.responsibleUserId || defaultUserId),
                                      split: 'done' as const,
                                      splitGroup: id,
                                  }))
                                : [item]
                        )
                    );
                    setExpandedId(cur => (cur === id ? null : cur));
                    setPreviewItem(cur => (cur?.localId === id ? null : cur));
                    return;
                }
                patchItem(id, { split: 'done' });
            }
            try {
                const result = await extractReceipt([target.file]);
                const fields = extractedToFields(result);
                commit(
                    itemsRef.current.map(item => {
                        if (item.localId !== id) return item;
                        const next = { ...item, ...fields };
                        next.status = statusAfterInput(next);
                        return next;
                    })
                );
            } catch {
                // Scan failed — let the user fill the fields by hand.
                patchItem(id, { status: 'analyzed' });
            }
        },
        [commit, defaultUserId, patchItem]
    );

    // Analyze pending items strictly one at a time (backend is 4 GB / 2 vCPU).
    const drainAnalyze = useCallback(async () => {
        if (drainingRef.current) return;
        drainingRef.current = true;
        try {
            while (true) {
                const next = itemsRef.current.find(item => item.status === 'pending');
                if (!next) break;
                await analyzeOne(next.localId);
            }
        } finally {
            drainingRef.current = false;
        }
    }, [analyzeOne]);

    const addFiles = useCallback(
        async (selected: FileList | File[] | null) => {
            if (!selected?.length) return;
            const accepted = Array.from(selected).filter(acceptFile);
            if (!accepted.length) {
                notify('Use JPEG, PNG, HEIC, WebP, or PDF files.', {
                    type: 'warning',
                });
                return;
            }
            const compressed = await compressReceiptImages(accepted);
            const newItems = compressed.map(file => newBulkItem(file, defaultUserId));
            commit([...itemsRef.current, ...newItems]);
            void drainAnalyze();
        },
        [commit, defaultUserId, drainAnalyze, notify]
    );

    const addProofs = useCallback(
        (id: string, selected: FileList | null) => {
            if (!selected?.length) return;
            const pdfs = Array.from(selected).filter(
                file => file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
            );
            if (!pdfs.length) {
                notify('Add a PDF.', { type: 'warning' });
                return;
            }
            const target = itemsRef.current.find(item => item.localId === id);
            if (!target) return;
            patchItem(id, { proofs: [...(target.proofs ?? []), ...pdfs] });
        },
        [notify, patchItem]
    );

    const removeProof = useCallback(
        (id: string, index: number) => {
            const target = itemsRef.current.find(item => item.localId === id);
            if (!target?.proofs) return;
            patchItem(id, { proofs: target.proofs.filter((_, itemIndex) => itemIndex !== index) });
        },
        [patchItem]
    );

    const removeItem = useCallback(
        (id: string) => {
            const target = itemsRef.current.find(item => item.localId === id);
            if (target) URL.revokeObjectURL(target.url);
            commit(itemsRef.current.filter(item => item.localId !== id));
            if (expandedId === id) setExpandedId(null);
        },
        [commit, expandedId]
    );

    const closeJoin = useCallback(() => {
        setJoinSourceId(null);
        setJoinTargetId(null);
    }, []);

    // Client-side only: no network call, no re-extraction. Re-validates against itemsRef at confirm time.
    const confirmJoin = useCallback(() => {
        if (joinSourceId == null || joinTargetId == null) return;
        const target = itemsRef.current.find(item => item.localId === joinTargetId);
        const next = joinItems(itemsRef.current, joinSourceId, joinTargetId);
        if (target && next !== itemsRef.current) {
            URL.revokeObjectURL(target.url);
            commit(next);
            setExpandedId(cur => (cur === joinTargetId ? null : cur));
            setPreviewItem(cur => (cur?.localId === joinTargetId ? null : cur));
        }
        closeJoin();
    }, [closeJoin, commit, joinSourceId, joinTargetId]);

    const clearUploaded = useCallback(() => {
        const removed = itemsRef.current.filter(item => item.status === 'uploaded');
        removed.forEach(item => URL.revokeObjectURL(item.url));
        commit(itemsRef.current.filter(item => item.status !== 'uploaded'));
    }, [commit]);

    const openCrop = useCallback((item: BulkItem) => {
        setCropId(item.localId);
        setCropItems([{ file: item.file, imageUrl: URL.createObjectURL(item.file) }]);
    }, []);

    const closeCrop = useCallback(() => {
        cropItems.forEach(item => URL.revokeObjectURL(item.imageUrl));
        setCropItems([]);
        setCropId(null);
    }, [cropItems]);

    const handleCropConfirm = useCallback(
        async (croppedFiles: File[]) => {
            const id = cropId;
            closeCrop();
            if (!id || !croppedFiles[0]) return;
            const [compressed] = await compressReceiptImages([croppedFiles[0]]);
            const prev = itemsRef.current.find(item => item.localId === id);
            if (prev) URL.revokeObjectURL(prev.url);
            // Replace the image and re-run analysis on the cropped receipt.
            patchItem(id, {
                file: compressed,
                url: URL.createObjectURL(compressed),
                isImage: true,
                status: 'pending',
                error: undefined,
            });
            void drainAnalyze();
        },
        [closeCrop, cropId, drainAnalyze, patchItem]
    );

    const uploadAll = useCallback(async () => {
        setUploading(true);
        let anySuccess = false;
        try {
            for (const item of [...itemsRef.current]) {
                if (item.status === 'uploaded' || item.split === 'failed') continue;
                setUploadingId(item.localId);
                try {
                    await uploadReceipt(buildReceiptFormData(item));
                    patchItem(item.localId, { status: 'uploaded', error: undefined });
                    anySuccess = true;
                } catch (error) {
                    patchItem(item.localId, {
                        status: 'error',
                        error:
                            error instanceof Error ? error.message : 'Upload failed',
                    });
                }
            }
        } finally {
            setUploadingId(null);
            setUploading(false);
            if (anySuccess) onUploaded?.();
        }
    }, [onUploaded, patchItem]);

    // Revoke object URLs on unmount.
    useEffect(
        () => () => {
            itemsRef.current.forEach(item => URL.revokeObjectURL(item.url));
        },
        []
    );

    const joinOptions = joinSourceId != null ? joinCandidates(items, joinSourceId) : [];
    const blockMessage = uploadBlockMessage(items);
    const allUploaded =
        items.length > 0 && items.every(item => item.status === 'uploaded');

    return (
        <Stack spacing={3}>
            <Box
                component="label"
                onDragEnter={event => {
                    event.preventDefault();
                    setDragActive(true);
                }}
                onDragOver={event => {
                    event.preventDefault();
                    setDragActive(true);
                }}
                onDragLeave={event => {
                    event.preventDefault();
                    if (event.currentTarget.contains(event.relatedTarget as Node)) return;
                    setDragActive(false);
                }}
                onDrop={event => {
                    event.preventDefault();
                    setDragActive(false);
                    if (!uploading) void addFiles(event.dataTransfer.files);
                }}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    minHeight: 160,
                    border: '2px dashed',
                    borderColor: dragActive ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    bgcolor: dragActive ? 'action.selected' : 'action.hover',
                    cursor: uploading ? 'default' : 'pointer',
                    px: 2,
                    py: 3,
                    opacity: uploading ? 0.6 : 1,
                    '&:hover': { borderColor: uploading ? 'divider' : 'primary.main' },
                }}
            >
                <UploadFileIcon color="action" sx={{ fontSize: 40 }} />
                <Typography>Drop receipts here, or click to choose</Typography>
                <Typography variant="body2" color="text.secondary">
                    Each file becomes its own receipt · JPEG, PNG, HEIC, WebP, or PDF
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Multi-page PDFs split into one receipt per page — up to 50 pages per file
                </Typography>
                <input
                    hidden
                    multiple
                    type="file"
                    accept="image/jpeg,image/png,image/heic,image/webp,application/pdf"
                    disabled={uploading}
                    onChange={event => {
                        void addFiles(event.target.files);
                        event.target.value = '';
                    }}
                />
            </Box>

            {allUploaded && (
                <Alert severity="success">All receipts uploaded.</Alert>
            )}
            {blockMessage && (
                <Typography variant="body2" color="text.secondary">
                    {blockMessage}
                </Typography>
            )}
            {items.some(item => item.status === 'uploaded') && (
                <Box>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<PlaylistRemoveIcon fontSize="small" />}
                        onClick={clearUploaded}
                    >
                        Clear uploaded
                    </Button>
                </Box>
            )}

            <Stack spacing={1.5}>
                {items.map(item => {
                    const meta = BULK_STATUS_META[item.status];
                    const expanded = expandedId === item.localId;
                    const isUploadingThis = uploadingId === item.localId;
                    const responsible = users.find(
                        user => String(user.id) === item.responsibleUserId
                    );
                    return (
                        <Card key={item.localId} variant="outlined">
                            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                    <Box
                                        sx={{
                                            width: 96,
                                            flexShrink: 0,
                                            position: 'relative',
                                        }}
                                    >
                                        {item.isImage ? (
                                            <Box
                                                component="img"
                                                src={item.url}
                                                alt={item.file.name}
                                                onClick={() => setPreviewItem(item)}
                                                sx={{
                                                    width: '100%',
                                                    height: 96,
                                                    objectFit: 'cover',
                                                    borderRadius: 1,
                                                    cursor: 'pointer',
                                                    display: 'block',
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                onClick={() => setPreviewItem(item)}
                                                sx={{
                                                    width: '100%',
                                                    height: 96,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: 1,
                                                    bgcolor: 'action.hover',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <Typography variant="caption">PDF</Typography>
                                            </Box>
                                        )}
                                        {item.isImage && !uploading && (
                                            <IconButton
                                                size="small"
                                                aria-label={`Crop ${item.file.name}`}
                                                onClick={() => openCrop(item)}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 2,
                                                    right: 2,
                                                    bgcolor: 'background.paper',
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>

                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            justifyContent="space-between"
                                        >
                                            <Typography noWrap sx={{ fontWeight: 500 }}>
                                                {item.file.name}
                                            </Typography>
                                            <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                                                {isUploadingThis && (
                                                    <CircularProgress size={16} aria-label="Uploading" />
                                                )}
                                                {(item.status === 'pending' || item.status === 'analyzing') && (
                                                    <CircularProgress size={16} aria-label="Processing" />
                                                )}
                                                <Chip size="small" label={meta.label} color={meta.color} />
                                            </Stack>
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {item.allocationType === 'project_cost'
                                                ? `Project cost${item.projectNo != null ? ` · #${item.projectNo}` : ''}`
                                                : item.allocationType === 'company_expense'
                                                  ? `Company expense${item.category ? ` · ${item.category}` : ''}${item.subcategory ? ` / ${item.subcategory}` : ''}`
                                                  : '—'}
                                            {item.merchant ? ` · ${item.merchant}` : ''}
                                            {item.amount ? ` · $${item.amount}` : ''}
                                            {item.transactionDate ? ` · ${item.transactionDate}` : ''}
                                            {responsible ? ` · ${responsible.name || responsible.email}` : ''}
                                            {item.description ? ` · ${item.description}` : ''}
                                        </Typography>
                                        {item.status === 'error' && item.error && (
                                            <Alert severity="error" sx={{ mt: 1 }}>
                                                {item.error}
                                            </Alert>
                                        )}
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                            {item.status !== 'uploaded' && (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={
                                                        expanded ? (
                                                            <CloseIcon fontSize="small" />
                                                        ) : (
                                                            <EditIcon fontSize="small" />
                                                        )
                                                    }
                                                    disabled={uploading}
                                                    onClick={() =>
                                                        setExpandedId(expanded ? null : item.localId)
                                                    }
                                                >
                                                    {expanded ? 'Close' : 'Edit'}
                                                </Button>
                                            )}
                                            {item.status !== 'uploaded' && !item.isImage && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    component="label"
                                                    startIcon={<UploadFileIcon fontSize="small" />}
                                                    disabled={uploading}
                                                >
                                                    Add PDF
                                                    <input
                                                        hidden
                                                        type="file"
                                                        accept="application/pdf"
                                                        multiple
                                                        onChange={event => {
                                                            addProofs(item.localId, event.target.files);
                                                            event.target.value = '';
                                                        }}
                                                    />
                                                </Button>
                                            )}
                                            {item.status !== 'uploaded' && !!item.splitGroup && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<CallMergeIcon fontSize="small" />}
                                                    disabled={uploading || item.status === 'analyzing'}
                                                    onClick={() => {
                                                        setJoinSourceId(item.localId);
                                                        setJoinTargetId(null);
                                                    }}
                                                >
                                                    Join page
                                                </Button>
                                            )}
                                            {item.status !== 'uploaded' && (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="error"
                                                    startIcon={<DeleteOutlineIcon fontSize="small" />}
                                                    disabled={uploading}
                                                    onClick={() => removeItem(item.localId)}
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                        </Stack>
                                        {(item.proofs ?? []).length > 0 && (
                                            <Stack spacing={0.5} sx={{ mt: 1 }}>
                                                {(item.proofs ?? []).map((proof, index) => (
                                                    <Stack
                                                        key={`${proof.name}-${index}`}
                                                        direction="row"
                                                        spacing={1}
                                                        alignItems="center"
                                                    >
                                                        <Typography variant="body2" color="text.secondary" noWrap>
                                                            Back page: {proof.name}
                                                        </Typography>
                                                        {item.status !== 'uploaded' && (
                                                            <Button
                                                                size="small"
                                                                disabled={uploading}
                                                                onClick={() => removeProof(item.localId, index)}
                                                            >
                                                                Remove
                                                            </Button>
                                                        )}
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        )}
                                        <Collapse in={expanded} unmountOnExit>
                                            <Box sx={{ mt: 2 }}>
                                                <ReceiptFieldsEditor
                                                    value={pickFields(item)}
                                                    onChange={fields => editItem(item.localId, fields)}
                                                    categories={categories}
                                                    users={users}
                                                    disabled={uploading}
                                                />
                                                <Button
                                                    size="small"
                                                    startIcon={<CloseIcon fontSize="small" />}
                                                    sx={{ mt: 1 }}
                                                    onClick={() => setExpandedId(null)}
                                                >
                                                    Close
                                                </Button>
                                            </Box>
                                        </Collapse>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    );
                })}
            </Stack>

            {items.length > 0 && (
                <Box>
                    <Button
                        variant="contained"
                        disabled={uploading || !canUpload(items)}
                        startIcon={
                            uploading ? (
                                <SyncIcon sx={{ animation: 'spin 1s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
                            ) : undefined
                        }
                        onClick={() => void uploadAll()}
                    >
                        {uploading ? 'Uploading…' : 'Upload receipts'}
                    </Button>
                </Box>
            )}

            <Dialog
                open={previewItem != null}
                onClose={() => setPreviewItem(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogContent sx={{ p: 0 }}>
                    {previewItem?.isImage ? (
                        <Box
                            component="img"
                            src={previewItem.url}
                            alt={previewItem.file.name}
                            sx={{ width: '100%', display: 'block' }}
                        />
                    ) : previewItem ? (
                        <Box
                            component="iframe"
                            src={previewItem.url}
                            title={previewItem.file.name}
                            sx={{ width: '100%', height: '80vh', border: 0 }}
                        />
                    ) : null}
                </DialogContent>
            </Dialog>

            <Dialog open={joinSourceId != null} onClose={closeJoin} maxWidth="xs" fullWidth>
                <DialogTitle>Join a page into this receipt</DialogTitle>
                <DialogContent dividers>
                    {joinOptions.length === 0 ? (
                        <Typography color="text.secondary">
                            No other pages from this PDF to join.
                        </Typography>
                    ) : (
                        <List disablePadding>
                            {joinOptions.map(option => (
                                <ListItemButton
                                    key={option.localId}
                                    selected={option.localId === joinTargetId}
                                    onClick={() => setJoinTargetId(option.localId)}
                                >
                                    <ListItemText
                                        primary={option.file.name}
                                        secondary={BULK_STATUS_META[option.status].label}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeJoin}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={joinTargetId == null}
                        onClick={confirmJoin}
                    >
                        Join
                    </Button>
                </DialogActions>
            </Dialog>

            <ReceiptImageCropDialog
                open={cropId != null}
                items={cropId != null ? cropItems : EMPTY_CROP_ITEMS}
                onClose={closeCrop}
                onCropConfirm={cropped => void handleCropConfirm(cropped)}
            />
        </Stack>
    );
};

const pickFields = (item: BulkItem): ReceiptFieldValues =>
    FIELD_KEYS.reduce((acc, key) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (acc as any)[key] = item[key];
        return acc;
    }, {} as ReceiptFieldValues);
